import React, { useMemo, useState } from "react";
import { Check, CloudOff, MessageSquare, Pencil, Plus, RefreshCw, Search, Trash2, X } from "lucide-react";
import type { ChannelSummary } from "core";
import { cn } from "lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";

/** Placeholder a conversation carries until it is named. */
const UNTITLED = "New chat";

/** §6 documents timestamps as strings; the deployed server sends epoch-ms numbers. */
export function toMs(value?: string | number): number {
  if (value === undefined || value === null || value === "") return NaN;
  if (typeof value === "number") return value;
  return /^\d+$/.test(value) ? Number(value) : new Date(value).getTime();
}

const DAY_MS = 86_400_000;

/** Buckets for the list, relative to the start of today. */
export function groupLabel(ms: number, now = Date.now()): string {
  if (Number.isNaN(ms)) return "Older";
  const startOfToday = new Date(now).setHours(0, 0, 0, 0);
  if (ms >= startOfToday) return "Today";
  if (ms >= startOfToday - DAY_MS) return "Yesterday";
  if (ms >= startOfToday - 7 * DAY_MS) return "Previous 7 days";
  if (ms >= startOfToday - 30 * DAY_MS) return "Previous 30 days";
  return "Older";
}

/**
 * The time shown on a row, scaled to how far back it is.
 *
 * Rows are grouped by day already, so inside "Today" the clock time is what
 * separates them, and further back the date is. Many conversations still share
 * a title, so this is often the only thing telling two rows apart.
 */
export function rowTime(ms: number, now = Date.now()): string {
  if (Number.isNaN(ms)) return "";
  const startOfToday = new Date(now).setHours(0, 0, 0, 0);
  if (ms >= startOfToday) {
    return new Date(ms).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  }
  const sameYear = new Date(ms).getFullYear() === new Date(now).getFullYear();
  return new Date(ms).toLocaleDateString(
    undefined,
    sameYear ? { month: "short", day: "numeric" } : { month: "short", day: "numeric", year: "numeric" },
  );
}

const lastActivity = (channel: ChannelSummary) => {
  const updated = toMs(channel.last_updated);
  return Number.isNaN(updated) ? toMs(channel.created_at) : updated;
};

function ListSkeleton() {
  return (
    <div className="space-y-1 px-3 pt-2" aria-hidden="true">
      {["78%", "62%", "70%", "54%", "66%"].map((width) => (
        <div key={width} className="px-2 py-2.5">
          <span className="skeleton-sweep block h-3 rounded-full" style={{ width }} />
        </div>
      ))}
    </div>
  );
}

interface ConversationListProps {
  channels: ChannelSummary[];
  activeChannelId: string | null;
  isLoading: boolean;
  error: string | null;
  /** The socket is not open: actions would only fail, so they are disabled. */
  disabled: boolean;
  onSelect: (channelId: string) => void;
  onNewChat: () => void;
  onRename: (channelId: string, title: string) => void;
  onDelete: (channelId: string) => void;
  onRetry: () => void;
  /** Present when the list is a panel that can be dismissed. */
  onClose?: () => void;
}

export function ConversationList({
  channels,
  activeChannelId,
  isLoading,
  error,
  disabled,
  onSelect,
  onNewChat,
  onRename,
  onDelete,
  onRetry,
  onClose,
}: ConversationListProps) {
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [deleting, setDeleting] = useState<ChannelSummary | null>(null);
  const [query, setQuery] = useState("");

  const commitRename = (channel: ChannelSummary) => {
    const title = draft.trim();
    // The list updates from the `channel_renamed` broadcast, not from here.
    if (title && title !== channel.title) onRename(channel.channel_id, title);
    setRenamingId(null);
  };

  const needle = query.trim().toLowerCase();
  const matches = useMemo(
    () =>
      needle
        ? channels.filter((channel) => (channel.title || UNTITLED).toLowerCase().includes(needle))
        : channels,
    [channels, needle],
  );

  const groups: { label: string; channels: ChannelSummary[] }[] = [];
  [...matches]
    .sort((a, b) => (lastActivity(b) || 0) - (lastActivity(a) || 0))
    .forEach((channel) => {
      const label = groupLabel(lastActivity(channel));
      const group = groups[groups.length - 1];
      if (group?.label === label) group.channels.push(channel);
      else groups.push({ label, channels: [channel] });
    });

  const renderRow = (channel: ChannelSummary) => {
    const isActive = channel.channel_id === activeChannelId;
    const title = channel.title || UNTITLED;

    if (renamingId === channel.channel_id) {
      return (
        <li key={channel.channel_id} className="flex items-center gap-1 px-1 py-1">
          <input
            autoFocus
            value={draft}
            maxLength={120}
            aria-label="Conversation title"
            onChange={(event) => setDraft(event.target.value)}
            onFocus={(event) => event.target.select()}
            onKeyDown={(event) => {
              if (event.key === "Enter") commitRename(channel);
              // Keep Escape on the field: the panel closes on it otherwise.
              if (event.key === "Escape") {
                event.stopPropagation();
                setRenamingId(null);
              }
            }}
            className="h-9 min-w-0 flex-1 rounded-lg border border-accent/50 bg-surface px-2.5 text-ui text-content outline-none ring-4 ring-accent/10 dark:bg-dark-800"
          />
          <button
            type="button"
            onClick={() => commitRename(channel)}
            className="rounded-md p-1.5 text-content-muted hover:bg-slate-100 hover:text-content focus-ring dark:hover:bg-dark-700"
            aria-label="Save title"
          >
            <Check className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => setRenamingId(null)}
            className="rounded-md p-1.5 text-content-muted hover:bg-slate-100 hover:text-content focus-ring dark:hover:bg-dark-700"
            aria-label="Cancel rename"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </li>
      );
    }

    const when = rowTime(lastActivity(channel));

    return (
      <li
        key={channel.channel_id}
        className={cn(
          "group/row relative flex items-center rounded-xl transition",
          isActive ? "bg-accent-muted dark:bg-dark-700" : "hover:bg-slate-100 dark:hover:bg-dark-800",
        )}
      >
        <button
          type="button"
          onClick={() => onSelect(channel.channel_id)}
          aria-current={isActive ? "page" : undefined}
          className="min-w-0 flex-1 rounded-xl px-3 py-2.5 text-left focus-ring"
          title={title}
        >
          <span
            className={cn(
              "block truncate text-ui",
              isActive ? "font-semibold text-accent dark:text-indigo-300" : "text-content",
            )}
          >
            {title}
          </span>
          {when && (
            <span className="mt-0.5 block truncate text-[11px] tabular-nums text-content-muted">{when}</span>
          )}
        </button>

        <span
          className={cn(
            "flex shrink-0 items-center pr-1.5 transition focus-within:opacity-100",
            "sm:opacity-0 sm:group-hover/row:opacity-100",
            isActive && "sm:opacity-100",
          )}
        >
          <button
            type="button"
            disabled={disabled}
            onClick={() => {
              setDraft(channel.title || "");
              setRenamingId(channel.channel_id);
            }}
            className="rounded-md p-1.5 text-content-muted hover:bg-slate-200/70 hover:text-content focus-ring disabled:opacity-40 dark:hover:bg-dark-600"
            aria-label={`Rename ${title}`}
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            disabled={disabled}
            onClick={() => setDeleting(channel)}
            className="rounded-md p-1.5 text-content-muted hover:bg-red-50 hover:text-red-600 focus-ring disabled:opacity-40 dark:hover:bg-red-900/20 dark:hover:text-red-400"
            aria-label={`Delete ${title}`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </span>
      </li>
    );
  };

  /* Search earns its place only once the list is long enough to scan badly. */
  const showSearch = channels.length > 8;

  return (
    <nav aria-label="Conversations" className="flex h-full min-h-0 flex-col">
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-line px-4 py-3.5 dark:border-dark-800">
        <h2 className="text-title-sm font-bold text-content">Chat history</h2>
        <div className="flex items-center gap-1">
          <span className="mr-1 text-[11px] tabular-nums text-content-muted">
            {channels.length > 0 && `${channels.length}`}
          </span>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close chat history"
              className="rounded-lg p-1.5 text-content-muted transition hover:bg-slate-100 hover:text-content focus-ring dark:hover:bg-dark-800"
            >
              <X className="h-[18px] w-[18px]" />
            </button>
          )}
        </div>
      </div>

      <div className="flex shrink-0 flex-col gap-2.5 p-3">
        <button
          type="button"
          onClick={onNewChat}
          className="flex h-10 w-full items-center justify-center gap-2 rounded-xl bg-accent px-3 text-ui font-semibold text-white transition hover:bg-accent-hover focus-ring"
        >
          <Plus className="h-4 w-4" /> New chat
        </button>

        {showSearch && (
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-content-muted" />
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                // Clear the box first; a second Escape closes the panel.
                if (event.key === "Escape" && query) {
                  event.stopPropagation();
                  setQuery("");
                }
              }}
              placeholder="Search conversations"
              aria-label="Search conversations"
              className="h-10 w-full rounded-xl border border-line bg-surface-muted pl-9 pr-3 text-ui text-content outline-none transition placeholder:text-content-muted focus:border-accent/50 focus:ring-4 focus:ring-accent/10 dark:bg-dark-800"
            />
          </div>
        )}
      </div>

      <div className="no-scrollbar min-h-0 flex-1 overflow-y-auto pb-3">
        {matches.length > 0 ? (
          groups.map((group) => (
            <section key={group.label} className="px-2 pb-2">
              <h3 className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                {group.label}
              </h3>
              <ul className="space-y-0.5">{group.channels.map(renderRow)}</ul>
            </section>
          ))
        ) : needle ? (
          <div className="px-5 py-8 text-center">
            <Search className="mx-auto mb-2 h-5 w-5 text-slate-300 dark:text-slate-600" />
            <p className="text-meta text-content-muted">
              Nothing matches “{query.trim()}”.
            </p>
          </div>
        ) : error ? (
          /* Says what is and is not affected, so an empty list does not read as data loss. */
          <div className="mx-3 mt-1 rounded-xl border border-line px-3 py-5 text-center">
            <CloudOff className="mx-auto mb-2 h-5 w-5 text-slate-400" />
            <p className="text-meta font-semibold text-content">Couldn’t load conversations</p>
            <p className="mt-1 text-meta text-content-muted">Your chats are safe. You can still start a new one.</p>
            <button
              type="button"
              onClick={onRetry}
              disabled={isLoading || disabled}
              className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-line px-2.5 py-1.5 text-meta font-semibold text-content-muted transition hover:bg-slate-50 focus-ring disabled:opacity-50 dark:hover:bg-dark-700"
            >
              <RefreshCw className={cn("h-3 w-3", isLoading && "animate-spin motion-reduce:animate-none")} />
              {isLoading ? "Retrying…" : "Retry"}
            </button>
          </div>
        ) : isLoading || disabled ? (
          <ListSkeleton />
        ) : (
          <div className="px-5 py-8 text-center">
            <MessageSquare className="mx-auto mb-2 h-5 w-5 text-slate-300 dark:text-slate-600" />
            <p className="text-meta text-content-muted">Your conversations will appear here.</p>
          </div>
        )}
      </div>

      {deleting && (
        <Dialog open onOpenChange={(open) => !open && setDeleting(null)}>
          <DialogContent size="sm">
            <DialogHeader>
              <DialogTitle>Delete this conversation?</DialogTitle>
              <DialogDescription>
                “{deleting.title || UNTITLED}” and all of its messages will be deleted. This can’t be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <button
                type="button"
                onClick={() => setDeleting(null)}
                className="h-10 rounded-xl px-3 text-ui font-semibold text-content-muted hover:text-content focus-ring"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  onDelete(deleting.channel_id);
                  setDeleting(null);
                }}
                className="h-10 rounded-xl bg-red-600 px-4 text-ui font-semibold text-white hover:bg-red-700 focus-ring"
              >
                Delete
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </nav>
  );
}
